/// <reference types="vite/client" />

/**
 * Environment variable accessor for the SignalSentry client app.
 *
 * Centralizes all VITE_* environment variable access with fallback defaults.
 * Import this object instead of accessing import.meta.env directly so that
 * tests can override values via vi.stubEnv() without scattering env access.
 *
 * Required env vars (set in .env.development or deployment config):
 *   VITE_FLEET_API_URL - Fleet API REST base URL
 *   VITE_FLEET_WS_URL  - Fleet API WebSocket base URL
 *   VITE_APP_ENV       - App environment identifier
 *
 * Optional map-source overrides (air-gapped / internal tile server deploys):
 *   VITE_MAP_SATELLITE_TILES_URL - raster tile template for the satellite
 *     basemap. Default is the public Esri World Imagery service so a normal
 *     production build is byte-identical to before this knob existed.
 *   VITE_MAP_OSM_TILES_URL - single raster tile template for the OSM fallback
 *     basemap. Default is empty: an empty value means "use the built-in
 *     3-subdomain public OSM array" (default unchanged). When set, callers
 *     treat it as a single tile-template URL.
 *   VITE_MAP_VECTOR_STYLE_URL - maplibre vector style URL for the frozen map
 *     snapshot. Default is the public openfreemap "liberty" style so the
 *     default build is unchanged.
 *
 * Runtime overrides (internal-cloud readiness, 2026-06-10):
 *   The five deployment-shaped values (both fleet URLs + the three map
 *   sources) are now lazy GETTERS that consult the optional boot-time
 *   /config.json (see src/lib/runtime-config.ts) FIRST and fall back to the
 *   build-time VITE_* value (with the same defaults as before) when no
 *   runtime override exists. On the public DigitalOcean deployment no
 *   config.json is shipped, so every getter resolves to exactly the same
 *   value the old `as const` object literal held - behavior is unchanged
 *   there. On an internal deployment, dropping a config.json next to
 *   index.html repoints the SAME build with no rebuild.
 *
 *   APP_ENV stays build-time only on purpose: it describes the artifact
 *   (which build was produced), not where it is deployed.
 */
import { getRuntimeConfig } from './runtime-config';

/**
 * Build-time fallback values, identical to the pre-runtime-config object
 * literal. Kept as a separate frozen bag so the getters below stay readable
 * and the defaults remain greppable in one place.
 */
const buildTimeEnv = {
  FLEET_API_URL: import.meta.env.VITE_FLEET_API_URL ?? 'http://localhost:8000',
  FLEET_WS_URL: import.meta.env.VITE_FLEET_WS_URL ?? 'ws://localhost:8000',
  APP_ENV: import.meta.env.VITE_APP_ENV ?? 'development',
  // Default == current hardcoded Esri World Imagery URL, so a normal build is
  // byte-identical. Only an explicit VITE_ override points it elsewhere.
  MAP_SATELLITE_TILES_URL:
    import.meta.env.VITE_MAP_SATELLITE_TILES_URL ??
    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
  // Empty default is intentional: callers fall back to the built-in
  // 3-subdomain public OSM array (default unchanged). A non-empty value is a
  // single tile-template URL for an internal tile server.
  MAP_OSM_TILES_URL: import.meta.env.VITE_MAP_OSM_TILES_URL ?? '',
  // Default == current hardcoded openfreemap "liberty" vector style URL, so
  // the default build is unchanged. Only an explicit override repoints it.
  MAP_VECTOR_STYLE_URL:
    import.meta.env.VITE_MAP_VECTOR_STYLE_URL ?? 'https://tiles.openfreemap.org/styles/liberty',
} as const;

/**
 * Environment accessor. Property READS are lazy: each getter consults the
 * runtime config (when /config.json was loaded at boot) and falls back to
 * the build-time value. Consumers keep using `env.FLEET_API_URL` etc.
 * exactly as before - the getter shape is a drop-in for the old literal.
 */
export const env = {
  /** Fleet API REST base URL (runtime config.json fleetApiUrl wins). */
  get FLEET_API_URL(): string {
    return getRuntimeConfig()?.fleetApiUrl ?? buildTimeEnv.FLEET_API_URL;
  },
  /** Fleet API WebSocket base URL (runtime config.json fleetWsUrl wins). */
  get FLEET_WS_URL(): string {
    return getRuntimeConfig()?.fleetWsUrl ?? buildTimeEnv.FLEET_WS_URL;
  },
  /** Build-time app environment id (NOT runtime-overridable; see header). */
  get APP_ENV(): string {
    return buildTimeEnv.APP_ENV;
  },
  /** Satellite tile template (runtime config.json mapSatelliteTilesUrl wins). */
  get MAP_SATELLITE_TILES_URL(): string {
    return getRuntimeConfig()?.mapSatelliteTilesUrl ?? buildTimeEnv.MAP_SATELLITE_TILES_URL;
  },
  /** OSM fallback tile template (runtime config.json mapOsmTilesUrl wins). */
  get MAP_OSM_TILES_URL(): string {
    return getRuntimeConfig()?.mapOsmTilesUrl ?? buildTimeEnv.MAP_OSM_TILES_URL;
  },
  /** Vector style URL (runtime config.json mapVectorStyleUrl wins). */
  get MAP_VECTOR_STYLE_URL(): string {
    return getRuntimeConfig()?.mapVectorStyleUrl ?? buildTimeEnv.MAP_VECTOR_STYLE_URL;
  },
};

/** Type guard: true if running in production. */
export const isProd = env.APP_ENV === 'production';

/** Type guard: true if running in development. */
export const isDev = env.APP_ENV === 'development';
